import { useState } from "react";
import type { FormEvent } from "react";

const SUB_URL = "https://readdy.ai/api/form/dae38jhtto0ceqqngoog";
const TIP_URL = "https://readdy.ai/api/form/dae38jhtto0ceqqngop0";

type Status = { state: "idle" | "success" | "error"; msg: string };

async function postForm(url: string, fd: FormData, hpName: string): Promise<{ ok: boolean; msg: string }> {
  // 反机器人：若蜜罐被填充，直接返回通用成功提示，不向服务器提交
  const hp = (fd.get(hpName) as string) || "";
  if (hp.trim() !== "") {
    return { ok: true, msg: "已收到你的信息，我们会尽快处理。" };
  }
  const params = new URLSearchParams();
  fd.forEach((value, key) => {
    if (key !== hpName) params.append(key, String(value));
  });
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params.toString(),
    });
    const responseText = await response.text();
    let parsed: any = {};
    try {
      parsed = JSON.parse(responseText);
    } catch {
      /* 忽略非 JSON 响应 */
    }
    const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
    const code = parsed?.code;
    const spamLike = typeof msg === "string" && /spam|form data is spam/i.test(msg);
    if (!response.ok || code !== "OK" || spamLike) {
      return { ok: false, msg: typeof msg === "string" && msg ? msg : "提交失败，请稍后重试。" };
    }
    return { ok: true, msg: typeof msg === "string" && msg ? msg : "提交成功，感谢支持。" };
  } catch {
    return { ok: false, msg: "网络异常，请检查连接后重试。" };
  }
}

export default function CtaForms() {
  const [sub, setSub] = useState<Status>({ state: "idle", msg: "" });
  const [tip, setTip] = useState<Status>({ state: "idle", msg: "" });

  async function onSubmitSubscribe(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const res = await postForm(SUB_URL, fd, "company_alt");
    if (res.ok) {
      form.reset();
      setSub({ state: "success", msg: res.msg });
    } else {
      setSub({ state: "error", msg: res.msg });
    }
  }

  async function onSubmitTip(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const res = await postForm(TIP_URL, fd, "website_alt");
    if (res.ok) {
      form.reset();
      setTip({ state: "success", msg: res.msg });
    } else {
      setTip({ state: "error", msg: res.msg });
    }
  }

  return (
    <section id="subscribe" className="scroll-mt-16 bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <form
            id="subscribe-form"
            data-readdy-form
            name="看料网订阅电子信"
            onSubmit={onSubmitSubscribe}
            className="flex flex-col rounded-lg border border-background-200 bg-background-950 p-6 md:p-8"
          >
            <span className="grid h-11 w-11 place-items-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-mail-send-line text-lg" />
            </span>
            <h3 className="mt-4 font-heading text-2xl font-black text-background-50">订阅看料网</h3>
            <p className="mt-2 text-sm leading-relaxed text-background-200/80">
              留下邮箱，第一时间收到头条深读与真相档案的更新提醒，不错过任何一条关键信号。
            </p>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                name="email"
                required
                placeholder="请输入你的邮箱"
                className="w-full rounded-md border border-background-200/20 bg-background-100/10 px-4 py-3 text-sm text-background-50 placeholder:text-background-300 outline-none transition-colors focus:border-accent-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-600"
              >
                立即订阅
              </button>
            </div>
            <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="kanliao-hp" />

            {sub.state !== "idle" && (
              <p
                className={`mt-3 flex items-center gap-1.5 text-sm ${
                  sub.state === "success" ? "text-accent-300" : "text-primary-400"
                }`}
              >
                <i className={sub.state === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
                {sub.msg}
              </p>
            )}
            <p className="mt-4 text-xs text-background-400">仅用于看料网内容推送，可随时退订。</p>
          </form>

          <form
            id="tip-form"
            data-readdy-form
            name="看料网线索投稿"
            onSubmit={onSubmitTip}
            className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
          >
            <span className="grid h-11 w-11 place-items-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-lightbulb-flash-line text-lg" />
            </span>
            <h3 className="mt-4 font-heading text-2xl font-black text-foreground-950">线索投稿</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              手头有值得深挖的公开线索或选题？把原始出处一并告诉我们，编辑部会依据公开信息复核后跟进。
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <input
                type="text"
                name="name"
                placeholder="你的称呼"
                className="rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
              />
              <input
                type="email"
                name="email"
                placeholder="联系方式（邮箱）"
                className="rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
              />
            </div>
            <input
              type="text"
              name="subject"
              placeholder="线索标题 / 选题方向"
              className="mt-3 rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
            />
            <textarea
              name="message"
              rows={4}
              maxLength={500}
              placeholder="尽量描述线索内容与可交叉核对的原始出处（不超过 500 字）"
              className="mt-3 w-full resize-none rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm leading-relaxed outline-none transition-colors focus:border-primary-400"
            />
            <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="kanliao-hp" />

            {tip.state !== "idle" && (
              <p
                className={`mt-3 flex items-center gap-1.5 text-sm ${
                  tip.state === "success" ? "text-primary-600" : "text-primary-500"
                }`}
              >
                <i className={tip.state === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
                {tip.msg}
              </p>
            )}

            <button
              type="submit"
              className="mt-5 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              提交线索
              <i className="ri-arrow-right-line" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}